// Save queue for the bizSAFE audit worksheet (Audit Execution — two auditors, patchy Wi-Fi, Nov 2026).
// The same rules as the Internal Audit queue (lib/iaSaveQueue.js), for the bizSAFE worksheet's own records. Every change
// is put here FIRST, then saved. Nothing is lost when a save fails: the change stays queued (also in this device's
// storage, so it survives a page reload) and is retried until it is saved. Only the boxes that were changed are sent —
// never the whole page — so two auditors (or two tabs) can't overwrite each other's work.
//
//   put('resp', itemId, 'remarks', value, base)    a checklist answer            (one record per checklist question)
//   put('iv',   rowId,  'response', value, base)   an interview row
//   put('part', rowId,  'name', value, base)       an attendee row (opening or closing meeting)
//   put('fm',   jobId,  'strengths', value, base)  the Audit Observations Summary (one per job)
//   put('cond', jobId,  'closing_at', value, base) the opening / closing meeting record (one per job)
//
// Each box also carries the value it showed BEFORE it was changed ("base"). The database saves a box only if nobody
// else changed it since (otherwise the newer saved value is kept and onConflict tells the auditor) — so a change that
// waited on an offline laptop can't overwrite the co-auditor's newer work. One save call per record.
//
// A newer change to the same box replaces the older one. An entry is removed only if it wasn't changed again while it
// was being saved. Anything that MIGHT work later is kept and retried — no connection, an expired login (after a laptop
// slept offline), a permission hiccup, and "the report is locked" (kept until the lead unlocks or the auditor chooses to
// discard). A refusal that can never succeed is dropped and reported through onRejected: our own refusals (e.g.
// "already submitted"), a broken data rule (23xxx), a value the database can't take (22xxx), and the save function
// not being found on the database (PGRST2xx — e.g. the website went live before the SQL).
const keepAndRetry = e => {
  if (!e) return true
  const code = String(e.code || '')
  const msg = String(e.message || e)
  if (code === 'P0001') return /sign in|locked/i.test(msg)   // our own refusals: only these two can still succeed
  if (/^2[23]/.test(code)) return false                      // bad value / data rule broken — retrying won't help
  if (/^PGRST2/.test(code)) return false                     // the save function isn't there — retrying won't help
  return true                                                // network / login / permission (42501): try again later
}

export function createRowSaveQueue({ storageKey, saveRow, onChange, onRejected, onConflict }) {
  const items = new Map()   // key → { kind, rowId, field, value, seq, base?, hasBase? }
  let seq = 0
  let running = null
  let saves = 0          // successful saves so far — lets the page ignore a refresh that started before a save landed
  let lockedHeld = false // the last attempt was refused because the report is signed and locked
  let lastKept = false   // the last attempt left something unsaved because a save failed (not just typed meanwhile)
  let failRuns = 0       // attempts in a row that left something unsaved because a save failed
  const keyOf = (kind, rowId, field) => `${kind}:${rowId}:${field}`
  const rowKey = (kind, rowId) => `${kind}:${rowId}`
  const persist = () => {
    try {
      if (items.size) localStorage.setItem(storageKey, JSON.stringify([...items.values()]))
      else localStorage.removeItem(storageKey)
    } catch (e) { /* private window / storage full — the queue still works in memory */ }
  }
  const changed = () => { persist(); onChange && onChange(items.size) }
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || '[]')
    if (Array.isArray(saved)) saved.forEach(e => { if (e && e.kind && e.rowId && e.field) items.set(keyOf(e.kind, e.rowId, e.field), { ...e, seq: ++seq }) })
  } catch (e) { /* unreadable — start empty */ }

  // `base`: the value shown before this change. A later change to the same box keeps the FIRST base — the value this
  // device last saw saved — until it is saved.
  function put(kind, rowId, field, value, base) {
    const k = keyOf(kind, rowId, field)
    const prev = items.get(k)
    const hasBase = prev ? !!prev.hasBase : base !== undefined
    items.set(k, { kind, rowId, field, value, seq: ++seq, ...(hasBase ? { hasBase, base: prev ? prev.base : base } : {}) })
    changed()
  }
  const removeIfUnchanged = list => list.forEach(e => { const k = keyOf(e.kind, e.rowId, e.field); if (items.get(k)?.seq === e.seq) items.delete(k) })

  async function run() {
    const all = [...items.values()]
    if (!all.length) return true
    let kept = false
    lockedHeld = false
    const fail = (list, err) => {
      if (keepAndRetry(err)) { kept = true; if (/locked/i.test(String(err?.message || ''))) lockedHeld = true; return }
      removeIfUnchanged(list)                      // refused for good — retrying would never work
      onRejected && onRejected(err, list)
    }
    const groups = new Map()
    all.forEach(e => { const g = rowKey(e.kind, e.rowId); if (!groups.has(g)) groups.set(g, []); groups.get(g).push(e) })
    for (const list of groups.values()) {
      const { kind, rowId } = list[0]
      try {
        const bases = Object.fromEntries(list.filter(e => e.hasBase).map(e => [e.field, e.base ?? null]))
        const res = await saveRow(kind, rowId, Object.fromEntries(list.map(e => [e.field, e.value])), bases)
        const conflicts = (res && res.conflicts) || {}
        removeIfUnchanged(list); saves++
        // A box changed again while saving stays queued: its base is now what is saved (ours, or the newer one kept).
        list.forEach(e => {
          const cur = items.get(keyOf(e.kind, e.rowId, e.field))
          if (cur && cur.hasBase) cur.base = e.field in conflicts ? (conflicts[e.field]?.value ?? null) : e.value
        })
        list.filter(e => e.field in conflicts).forEach(e => onConflict && onConflict(kind, rowId, e.field, e.value, conflicts[e.field]))
      }
      catch (err) { fail(list, err) }
    }
    lastKept = kept
    failRuns = kept ? failRuns + 1 : 0
    changed()
    return !kept && items.size === 0
  }

  // Save everything queued now. Calls made while a save is running wait for it, then save anything newer.
  async function flush() {
    while (running) { try { await running } catch (e) { /* handled inside run */ } }
    running = run()
    try { return await running } finally { running = null }
  }

  // Queued (not yet saved) values of one record, to show on screen over what the server has.
  function pending(kind, rowId) {
    const out = {}
    items.forEach(e => { if (e.kind === kind && e.rowId === rowId) out[e.field] = e.value })
    return out
  }

  return {
    put, flush, pending,
    size: () => items.size,
    saves: () => saves,
    busy: () => !!running,
    lockedHeld: () => lockedHeld && items.size > 0,
    lastFailed: () => lastKept && items.size > 0,
    // Saves have kept failing (more than one retry in a row) — the page then offers "Discard" as well.
    stuck: () => failRuns > 1 && items.size > 0,
    // "Discard unsaved changes on this device" — the auditor's own escape hatch.
    clear: () => { items.clear(); lockedHeld = false; lastKept = false; failRuns = 0; changed() },
    // One record with this device's unsaved changes on top.
    overlayRow: (kind, rowId, row) => ({ ...(row || {}), ...pending(kind, rowId) }),
    // A map of records (rowId → record) with this device's unsaved changes on top; a record only queued here is added.
    overlayMap: (kind, map, makeRow = rowId => ({})) => {
      const out = { ...map }
      items.forEach(e => { if (e.kind === kind) out[e.rowId] = { ...(out[e.rowId] || makeRow(e.rowId)), [e.field]: e.value } })
      return out
    },
    has: (kind, rowId, field) => items.has(keyOf(kind, rowId, field)),
    hasRow: (kind, rowId) => [...items.values()].some(e => e.kind === kind && e.rowId === rowId),
  }
}
